// Registry of API models that can be populated from an uploaded spreadsheet.
//
// Mirrors the ChatQueryDescriptor pattern in Dashboard/ChatQueryRegistry.ts: adding a
// new importable model should be one `descriptors.push({ ... })`, not another bespoke
// upload component.
//
// WHY THESE FIELD LISTS ARE HAND-WRITTEN
// --------------------------------------
// The generated clients carry no runtime property metadata. Every interface in
// src/Generated/**/api.ts is compile-time only and fully erased by the build, and for
// JSON-body endpoints the payload is handed straight to serializeDataIfNeeded, so not
// a single field name survives into the bundle. A model's field list therefore cannot
// be enumerated at runtime. Codegen off the JSDoc in api.ts would recover names and
// types but none of what actually matters here - display labels, alias lists, which
// runtime enum backs a field, how to resolve a name to an entity id, or how a flat row
// splits across a nested payload. So: written by hand, deliberately.
//
// Enum-valued fields DO reference the runtime `as const` objects from the generated
// client (Unit, Currency, Side, ...), which are real JS values and do survive, so
// value dropdowns stay correct without hand-listing legal values.
import {
    ComponentType,
    Currency,
    OptionStyle,
    OptionType,
    PositionType,
    Side,
    Unit,
} from '../../../../Generated/Commodity-Management-Api';
import type { ColumnMatchable } from '../../../../Helpers';

/** How a field's values should be parsed, validated and edited. */
export type ImportFieldKind =
    | 'text'
    | 'number'
    | 'date'
    | 'month'
    | 'year'
    | 'enum'
    | 'entity';

/** Customer-data collection an 'entity' field resolves against. */
export type ImportEntitySource =
    | 'commodities'
    | 'counterparties'
    | 'locations'
    | 'portfolios'
    | 'budgetRounds'
    | 'budgetPricingStrategies';

export interface ImportFieldDescriptor extends ColumnMatchable {
    key: string;
    label: string;
    kind: ImportFieldKind;
    /** Required fields produce a blocking error when empty; others a warning at most. */
    required: boolean;
    aliases: string[];
    /** Runtime enum object from the generated client, for kind === 'enum'. */
    enumValues?: Record<string, string>;
    /** Customer-data collection, for kind === 'entity'. */
    entitySource?: ImportEntitySource;
    /**
     * Customer setting that gates this field. When the site has the setting off the
     * column is hidden and the field is not treated as required.
     */
    settingKey?: 'positionsByCounterparty' | 'positionsByLocation' | 'positionsByPortfolio'
        | 'budgetsByCounterparty' | 'budgetsByLocation' | 'budgetsByPortfolio'
        | 'usesBudgetRounds';
    /** Shown as helper text in the mapping table and review grid. */
    hint?: string;
    /**
     * Marks the month/year halves of one logical date. Fields sharing a group may be
     * fed by the SAME file column when that column holds full dates - a single
     * "Budget Date" column fills both Budget Month and Budget Year, each parsing the
     * part it needs. Outside a group, two fields sharing one column is a mistake worth
     * flagging.
     */
    dateGroup?: 'budget' | 'contract';
    /**
     * Field is not read from the file - it is computed from other fields (see
     * ImportModelDescriptor.derive). It still gets a review-grid column so the user can
     * see and override what was derived, but it never appears in the mapping table.
     */
    derived?: boolean;
}

export interface ImportModelDescriptor {
    key: string;
    singular: string;
    plural: string;
    /** Weighted toward this model when present in the file name or sheet names. */
    keywords: string[];
    /** Subtracted when scoring - e.g. budget files often also mention "position". */
    antiKeywords?: string[];
    fields: ImportFieldDescriptor[];
}

// --------------------------------------------------------------------------
// Positions
//
// Positions have no standalone create endpoint. They are nested inside
// UpdateDealTicketRequest and written with CommodityManagementApi.addTrade, and the
// ticket - not the position - carries tradeDate, commodityId, counterpartyId,
// portfolioId and dealTicketNumber. A flat spreadsheet row therefore has to be split
// across two levels and rows grouped into tickets at submit time. The fields below are
// the flat row as the user sees it; the ticket/position split happens in the submitter.
// --------------------------------------------------------------------------
const POSITION_FIELDS: ImportFieldDescriptor[] = [
    {
        key: 'commodity', label: 'Commodity', kind: 'entity', required: true,
        entitySource: 'commodities',
        aliases: ['Commodity', 'Commodity Name', 'Product', 'Product Name'],
    },
    {
        key: 'counterparty', label: 'Counterparty', kind: 'entity', required: true,
        entitySource: 'counterparties', settingKey: 'positionsByCounterparty',
        aliases: [
            'Counterparty', 'Counterparty Name', 'Vendor', 'Supplier',
            'Customer', 'Trading Partner', 'Trade Partner', 'Broker',
        ],
    },
    {
        key: 'location', label: 'Location', kind: 'entity', required: true,
        entitySource: 'locations', settingKey: 'positionsByLocation',
        aliases: [
            'Location', 'Location Name', 'Plant', 'Plant Code', 'Site', 'Site Name',
            'Origin', 'Destination', 'Elevator', 'Facility',
        ],
    },
    {
        key: 'portfolio', label: 'Portfolio', kind: 'entity', required: false,
        entitySource: 'portfolios', settingKey: 'positionsByPortfolio',
        aliases: ['Portfolio', 'Portfolio Name', 'Account', 'Strategy', 'Book', 'Trading Book'],
    },
    {
        key: 'tradeDate', label: 'Trade Date', kind: 'date', required: true,
        aliases: [
            'Trade Date', 'TradeDate', 'Date', 'Booked Date', 'Entry Date',
            'Trade Day', 'Transaction Date', 'Effective Date',
        ],
        hint: 'Lives on the deal ticket - rows sharing a trade date and counterparty group together.',
    },
    {
        key: 'dealTicketNumber', label: 'Deal Ticket Number', kind: 'text', required: false,
        aliases: [
            'Deal Ticket', 'DealTicket', 'Deal Ticket Number', 'Deal Number', 'Deal ID',
            'Ticket', 'Ticket Number', 'Ticket No', 'Ticket #',
            'Trade Number', 'Trade ID', 'Confirmation Number',
            'PO', 'PO Number', 'Reference', 'Reference Number',
        ],
        hint: 'Blank rows are grouped into an auto-generated ticket per counterparty and trade date.',
    },
    {
        key: 'volume', label: 'Volume', kind: 'number', required: true,
        aliases: [
            'Volume', 'Quantity', 'Qty', 'Quantity Bushels', 'Quantity Contracts',
            'Quantity Tons', 'Volume Bushels', 'Volume Contracts', 'Volume Tons',
            'Bushels', 'Tons', 'Tonnes', 'Lots', 'Contracts', 'Number of Contracts',
        ],
    },
    {
        key: 'price', label: 'Price', kind: 'number', required: false,
        aliases: [
            'Price', 'Rate', 'Cost', 'Trade Price', 'Unit Price', 'Price Per Unit',
            'Fixed Price', 'Basis Price', 'Net Price', 'Board Price',
        ],
    },
    {
        key: 'unit', label: 'Unit', kind: 'enum', required: true, enumValues: Unit,
        aliases: ['Unit', 'Units', 'UOM', 'Unit Of Measure', 'Measure', 'Measurement'],
    },
    {
        key: 'currency', label: 'Currency', kind: 'enum', required: true, enumValues: Currency,
        aliases: ['Currency', 'Ccy', 'CCY', 'Curr', 'Currency Code'],
    },
    {
        key: 'side', label: 'Side', kind: 'enum', required: true, enumValues: Side,
        aliases: [
            'Side', 'Buy/Sell', 'Buy Sell', 'Buy or Sell', 'B/S', 'Direction',
            'Trade Side', 'Position Side', 'Action',
        ],
    },
    {
        key: 'positionType', label: 'Position Type', kind: 'enum', required: false,
        enumValues: PositionType,
        aliases: [
            'Position Type', 'PositionType', 'Type', 'Instrument', 'Instrument Type',
            'Trade Type', 'Product Type', 'Contract Type',
        ],
    },
    {
        key: 'budgetMonth', label: 'Budget Month', kind: 'month', required: true,
        dateGroup: 'budget',
        aliases: ['Budget Month', 'BudgetMonth', 'Delivery Month', 'Budget Date', 'Delivery Date'],
    },
    {
        key: 'budgetYear', label: 'Budget Year', kind: 'year', required: true,
        dateGroup: 'budget',
        aliases: ['Budget Year', 'BudgetYear', 'Delivery Year'],
    },
    {
        key: 'contractMonth', label: 'Contract Month', kind: 'month', required: false,
        dateGroup: 'contract',
        aliases: ['Contract Month', 'ContractMonth', 'Futures Month', 'Maturity Month', 'Contract Date'],
    },
    {
        key: 'contractYear', label: 'Contract Year', kind: 'year', required: false,
        dateGroup: 'contract',
        aliases: ['Contract Year', 'ContractYear', 'Futures Year', 'Maturity Year'],
    },
    {
        key: 'optionStrike', label: 'Option Strike', kind: 'number', required: false,
        aliases: ['Option Strike', 'OptionStrike', 'Strike', 'Strike Price', 'StrikePrice'],
    },
    {
        key: 'optionType', label: 'Option Type', kind: 'enum', required: false, enumValues: OptionType,
        aliases: ['Option Type', 'OptionType', 'Call/Put', 'Call Put', 'Put/Call', 'Put Call', 'C/P', 'P/C'],
    },
    {
        key: 'optionStyle', label: 'Option Style', kind: 'enum', required: false, enumValues: OptionStyle,
        aliases: ['Option Style', 'OptionStyle', 'Style', 'Exercise Style', 'ExerciseStyle'],
    },
];

// --------------------------------------------------------------------------
// Budgets -> CommodityManagementApi.updateBudgets (PUT, one batched call).
// budgetId 0 means insert. Prices are nested in budgetPrices[]; the flat row carries a
// single price which the submitter wraps into one UpdateBudgetPriceModel.
//
// Note: the legacy UploadBudgets.tsx mapper sets locationName, budgetRound and a
// top-level price on the object and hides it behind an `as UpdateBudgetModel` cast -
// none of those exist on the interface. Do not copy that shape.
// --------------------------------------------------------------------------
const BUDGET_FIELDS: ImportFieldDescriptor[] = [
    {
        key: 'commodity', label: 'Commodity', kind: 'entity', required: true,
        entitySource: 'commodities',
        aliases: ['Commodity', 'Commodity Name', 'Product', 'Product Name'],
    },
    {
        key: 'counterparty', label: 'Counterparty', kind: 'entity', required: false,
        entitySource: 'counterparties', settingKey: 'budgetsByCounterparty',
        aliases: ['Counterparty', 'Counterparty Name', 'Vendor', 'Supplier', 'Customer'],
    },
    {
        key: 'location', label: 'Location', kind: 'entity', required: false,
        entitySource: 'locations', settingKey: 'budgetsByLocation',
        aliases: ['Location', 'Location Name', 'Plant', 'Site', 'Site Name', 'Facility'],
    },
    {
        key: 'budgetMonth', label: 'Budget Month', kind: 'month', required: true,
        dateGroup: 'budget',
        aliases: ['Budget Month', 'BudgetMonth', 'Month', 'Delivery Month', 'Budget Date'],
    },
    {
        key: 'budgetYear', label: 'Budget Year', kind: 'year', required: true,
        dateGroup: 'budget',
        aliases: ['Budget Year', 'BudgetYear', 'Year', 'Delivery Year'],
    },
    {
        key: 'contractMonth', label: 'Contract Month', kind: 'month', required: false,
        dateGroup: 'contract',
        aliases: ['Contract Month', 'ContractMonth', 'Futures Month', 'Maturity Month'],
    },
    {
        key: 'contractYear', label: 'Contract Year', kind: 'year', required: false,
        dateGroup: 'contract',
        aliases: ['Contract Year', 'ContractYear', 'Futures Year', 'Maturity Year'],
    },
    {
        key: 'volume', label: 'Volume', kind: 'number', required: true,
        aliases: ['Volume', 'Quantity', 'Qty', 'Bushels', 'Tons', 'Tonnes', 'Budget Volume'],
    },
    {
        key: 'price', label: 'Price', kind: 'number', required: true,
        aliases: ['Price', 'Budget Price', 'Rate', 'Cost', 'Unit Price', 'Price Per Unit'],
        hint: 'Written as a single budget price component.',
    },
    {
        key: 'unit', label: 'Unit', kind: 'enum', required: true, enumValues: Unit,
        aliases: ['Unit', 'Units', 'UOM', 'Unit Of Measure', 'Measure'],
    },
    {
        key: 'currency', label: 'Currency', kind: 'enum', required: true, enumValues: Currency,
        aliases: ['Currency', 'Ccy', 'CCY', 'Curr', 'Currency Code'],
    },
    {
        key: 'budgetRound', label: 'Budget Round', kind: 'entity', required: false,
        entitySource: 'budgetRounds', settingKey: 'usesBudgetRounds',
        aliases: ['Budget Round', 'BudgetRound', 'Round', 'Round Name', 'Budget Version', 'Version'],
    },
    {
        // Required by UpdateBudgetModel but never present in the file - derived from
        // commodity + location + counterparty. Shown as an editable column so an
        // ambiguous derivation (0 or 2+ matching strategies) is visible and fixable
        // rather than crashing the way the legacy importer does.
        key: 'pricingStrategy', label: 'Pricing Strategy', kind: 'entity', required: true,
        entitySource: 'budgetPricingStrategies', derived: true,
        aliases: ['Pricing Strategy', 'PricingStrategy', 'Strategy', 'Price Strategy'],
        hint: 'Derived from commodity, location and counterparty. Set it manually when several strategies match.',
    },
    {
        key: 'note', label: 'Note', kind: 'text', required: false,
        aliases: ['Note', 'Notes', 'Comment', 'Comments', 'Remark', 'Remarks', 'Description'],
    },
];

// --------------------------------------------------------------------------
// Budget prices -> the only model that UPDATES rather than inserts.
//
// A file of nothing but prices has no volume, unit, currency or pricing strategy to
// create a budget with; it is priced against budgets that already exist. Rows are
// matched to them on commodity, location and budget month/year, and the price is written
// into that budget's budgetPrices. A price row is meaningless without a component type,
// so componentType is required - when the file has no column for it (the usual case,
// where a whole file is one component) it is left unmapped and set for every row at once
// on the review step.
// --------------------------------------------------------------------------
const BUDGET_PRICE_FIELDS: ImportFieldDescriptor[] = [
    {
        key: 'commodity', label: 'Commodity', kind: 'entity', required: true,
        entitySource: 'commodities',
        aliases: ['Commodity', 'Commodity Name', 'Product', 'Product Name'],
    },
    {
        key: 'location', label: 'Location', kind: 'entity', required: false,
        entitySource: 'locations', settingKey: 'budgetsByLocation',
        aliases: ['Location', 'Location Name', 'Plant', 'Site', 'Site Name', 'Facility'],
        hint: 'Blank matches budgets that have no location.',
    },
    {
        key: 'budgetMonth', label: 'Budget Month', kind: 'month', required: true,
        dateGroup: 'budget',
        aliases: ['Budget Month', 'BudgetMonth', 'Month', 'Delivery Month', 'Budget Date'],
    },
    {
        key: 'budgetYear', label: 'Budget Year', kind: 'year', required: true,
        dateGroup: 'budget',
        aliases: ['Budget Year', 'BudgetYear', 'Year', 'Delivery Year'],
    },
    {
        key: 'componentType', label: 'Component Type', kind: 'enum', required: true,
        enumValues: ComponentType,
        aliases: [
            'Component Type', 'ComponentType', 'Component', 'Price Type', 'PriceType',
            'Price Component', 'Type',
        ],
        hint: 'Which price component this is. Set it once for the whole file if the file does not say.',
    },
    {
        key: 'price', label: 'Price', kind: 'number', required: true,
        aliases: ['Price', 'Budget Price', 'Rate', 'Cost', 'Unit Price', 'Price Per Unit', 'Value'],
    },
    {
        key: 'unit', label: 'Unit', kind: 'enum', required: false, enumValues: Unit,
        aliases: ['Unit', 'Units', 'UOM', 'Unit Of Measure', 'Measure'],
        hint: "Defaults to the matched budget's unit.",
    },
];

// --------------------------------------------------------------------------
// Deliveries -> CommodityManagementApi.updateDeliveries (PUT, batched).
// deliveryId 0 means insert. Twenty flat fields, no nesting - the simplest of the
// three. siteGuid is filled from the active site, not from the file.
//
// 'recievingPlant' is misspelled on the wire; that is the real field name, keep it.
// --------------------------------------------------------------------------
const DELIVERY_FIELDS: ImportFieldDescriptor[] = [
    {
        key: 'postingDate', label: 'Posting Date', kind: 'date', required: true,
        aliases: ['Posting Date', 'PostingDate', 'Posted Date', 'Post Date', 'Date'],
    },
    {
        key: 'documentDate', label: 'Document Date', kind: 'date', required: false,
        aliases: ['Document Date', 'DocumentDate', 'Doc Date', 'Doc. Date'],
    },
    {
        key: 'plant', label: 'Plant', kind: 'text', required: true,
        aliases: ['Plant', 'Plant Code', 'Location', 'Site'],
    },
    {
        key: 'movementType', label: 'Movement Type', kind: 'number', required: true,
        aliases: ['Movement Type', 'MovementType', 'Mvt Type', 'Movement', 'Mvt'],
    },
    {
        key: 'movementTypeText', label: 'Movement Type Text', kind: 'text', required: true,
        aliases: ['Movement Type Text', 'MovementTypeText', 'Mvt Type Text', 'Movement Description'],
    },
    {
        key: 'material', label: 'Material', kind: 'text', required: true,
        aliases: ['Material', 'Material Number', 'Material Code', 'Item', 'SKU'],
    },
    {
        key: 'materialDescription', label: 'Material Description', kind: 'text', required: true,
        aliases: ['Material Description', 'MaterialDescription', 'Material Desc', 'Description', 'Item Description'],
    },
    {
        key: 'quantity', label: 'Quantity', kind: 'number', required: true,
        aliases: ['Quantity', 'Qty', 'Volume', 'Amount', 'Quantity Delivered'],
    },
    {
        key: 'baseUnitofMeasure', label: 'Base Unit of Measure', kind: 'text', required: true,
        aliases: ['Base Unit of Measure', 'BaseUnitofMeasure', 'Base UOM', 'BUn', 'UOM', 'Unit'],
    },
    {
        key: 'vendor', label: 'Vendor', kind: 'text', required: true,
        aliases: ['Vendor', 'Supplier', 'Vendor Name', 'Vendor Code', 'Counterparty'],
    },
    {
        key: 'purchaseOrder', label: 'Purchase Order', kind: 'text', required: true,
        aliases: ['Purchase Order', 'PurchaseOrder', 'PO', 'PO Number', 'Purchasing Document'],
    },
    {
        key: 'amountInLocation', label: 'Amount in Location Currency', kind: 'number', required: false,
        aliases: ['Amount in Location', 'AmountInLocation', 'Amt in LC', 'Amount LC', 'Amt in Loc Cur', 'Value'],
    },
    {
        key: 'reasonForMovement', label: 'Reason for Movement', kind: 'text', required: false,
        aliases: ['Reason for Movement', 'ReasonForMovement', 'Reason', 'Movement Reason'],
    },
    {
        key: 'storageLocation', label: 'Storage Location', kind: 'text', required: false,
        aliases: ['Storage Location', 'StorageLocation', 'SLoc', 'Storage Loc', 'Bin'],
    },
    {
        key: 'batch', label: 'Batch', kind: 'text', required: false,
        aliases: ['Batch', 'Batch Number', 'Lot', 'Lot Number'],
    },
    {
        key: 'customer', label: 'Customer', kind: 'text', required: false,
        aliases: ['Customer', 'Customer Name', 'Customer Code', 'Sold To'],
    },
    {
        // The legacy importer feeds this from the "Material Document" column, which is
        // what SAP exports actually call it - keep that alias first.
        key: 'order', label: 'Order / Material Document', kind: 'text', required: false,
        aliases: [
            'Material Document', 'MaterialDocument', 'Mat. Doc.', 'Mat Doc',
            'Order', 'Order Number', 'Sales Order', 'Delivery Order',
        ],
        hint: 'Rows sharing this value are aggregated into one delivery.',
    },
    {
        // Misspelled on the wire (DeliveryModel.recievingPlant) - the label is correct
        // for the user, the key matches the API.
        key: 'recievingPlant', label: 'Receiving Plant', kind: 'text', required: false,
        aliases: ['Receiving Plant', 'RecievingPlant', 'Receiving Plant Code', 'Dest Plant', 'Destination Plant'],
    },
];

export const IMPORT_MODELS: ImportModelDescriptor[] = [
    {
        key: 'positions',
        singular: 'position',
        plural: 'positions',
        keywords: ['position', 'trade', 'deal', 'ticket', 'futures', 'swap', 'option', 'hedge', 'fill'],
        antiKeywords: ['budget', 'forecast', 'delivery', 'shipment', 'goods receipt'],
        fields: POSITION_FIELDS,
    },
    {
        key: 'budgets',
        singular: 'budget',
        plural: 'budgets',
        keywords: ['budget', 'forecast', 'plan', 'round', 'target'],
        antiKeywords: ['position', 'trade', 'deal ticket', 'delivery', 'shipment'],
        fields: BUDGET_FIELDS,
    },
    {
        key: 'budgetPrices',
        singular: 'budget price',
        plural: 'budget prices',
        keywords: ['price', 'pricing', 'budget price', 'basis', 'adder', 'flat price'],
        antiKeywords: ['position', 'trade', 'deal ticket', 'delivery', 'shipment'],
        fields: BUDGET_PRICE_FIELDS,
    },
    {
        key: 'deliveries',
        singular: 'delivery',
        plural: 'deliveries',
        keywords: ['delivery', 'deliveries', 'shipment', 'movement', 'goods receipt', 'receipt', 'material', 'plant'],
        antiKeywords: ['budget', 'forecast', 'position', 'deal ticket'],
        fields: DELIVERY_FIELDS,
    },
];

export function getImportModel(key: string): ImportModelDescriptor | undefined {
    return IMPORT_MODELS.find(m => m.key === key);
}

/** Settings shape the field gating needs. Kept structural so callers can pass the
 *  Redux GetCustomerSettingsResponse straight through. */
export type ImportFieldSettings = Partial<Record<NonNullable<ImportFieldDescriptor['settingKey']>, boolean | undefined>>;

/**
 * Fields this site actually uses. A field gated behind a customer setting that is off
 * is dropped entirely - it should not appear as a mapping target, get a review-grid
 * column, or be enforced as required.
 */
export function getActiveFields(
    model: ImportModelDescriptor,
    settings: ImportFieldSettings | undefined
): ImportFieldDescriptor[] {
    return model.fields.filter(field => {
        if (!field.settingKey) return true;
        // Absent settings object means "not configured yet" - keep the field rather
        // than silently hiding columns the user's file does contain.
        if (!settings) return true;
        return settings[field.settingKey] !== false;
    });
}

/**
 * Fields a file column may be mapped onto. Excludes derived fields, which are computed
 * from other columns rather than read from the file.
 */
export function getMappableFields(
    model: ImportModelDescriptor,
    settings: ImportFieldSettings | undefined
): ImportFieldDescriptor[] {
    return getActiveFields(model, settings).filter(field => !field.derived);
}
